import { api } from "./api";
import type { GroupingSource } from "./metadoc-api";

export type DocumentGroupTagType =
  | "RECORDS_FOR_RATED_AGE"
  | "CMS_SUBMISSION"
  | "FROI";

export const DOCUMENT_GROUP_TAG_TYPES = {
  RECORDS_FOR_RATED_AGE: "RECORDS_FOR_RATED_AGE" as DocumentGroupTagType,
  CMS_SUBMISSION: "CMS_SUBMISSION" as DocumentGroupTagType,
  FROI: "FROI" as DocumentGroupTagType,
};

export const DOCUMENT_GROUP_TAG_LABELS: Record<DocumentGroupTagType, string> = {
  RECORDS_FOR_RATED_AGE: "Records for Rated Age",
  CMS_SUBMISSION: "CMS Submission",
  FROI: "FROI",
};

export type DocumentGroupTag = {
  id: number;
  document_group: number;
  type: DocumentGroupTagType;
  created: string;
};

export type CreateDocumentGroupTagArgs = {
  document_group: number;
  type: DocumentGroupTagType;
};

export type BulkAssignDocumentGroupTagsByDateRangeArgs = {
  metadoc: number;
  source: GroupingSource;
  type: DocumentGroupTagType;
  start_date: string;
  end_date: string;
};

export type BulkAssignDocumentGroupTagsByDateRangeResponse = {
  created: DocumentGroupTag[];
  already_tagged_count: number;
  undated_count: number;
};

const documentGroupTagApi = api.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    // create a new document group tag
    createDocumentGroupTag: build.mutation<
      DocumentGroupTag,
      CreateDocumentGroupTagArgs
    >({
      query: (data) => ({
        url: "api/v1/document-group-tags/",
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: [{ type: "DocumentGroupTags", id: "LIST" }],
    }),

    // get all document group tags for a specific metadoc
    getDocumentGroupTags: build.query<DocumentGroupTag[], number>({
      query: (metadocId) => ({
        url: "api/v1/document-group-tags/",
        method: "GET",
        params: {
          metadoc: metadocId,
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "DocumentGroupTags" as const,
                id,
              })),
              { type: "DocumentGroupTags", id: "LIST" },
            ]
          : [{ type: "DocumentGroupTags", id: "LIST" }],
    }),

    bulkAssignDocumentGroupTagsByDateRange: build.mutation<
      BulkAssignDocumentGroupTagsByDateRangeResponse,
      BulkAssignDocumentGroupTagsByDateRangeArgs
    >({
      query: (data) => ({
        url: "api/v1/document-group-tags/bulk-assign-by-date-range/",
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: [{ type: "DocumentGroupTags", id: "LIST" }],
    }),

    // delete a document group tag
    deleteDocumentGroupTag: build.mutation<void, number>({
      query: (id) => ({
        url: `api/v1/document-group-tags/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "DocumentGroupTags", id },
        { type: "DocumentGroupTags", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useBulkAssignDocumentGroupTagsByDateRangeMutation,
  useCreateDocumentGroupTagMutation,
  useGetDocumentGroupTagsQuery,
  useDeleteDocumentGroupTagMutation,
} = documentGroupTagApi;

export default documentGroupTagApi;
